import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { UserStateService } from 'src/app/auth/services/user-state.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { EditorToolsLoggingService } from '../services/editor-tools-logging.service';
import {
  ImageAiService,
  ImageAiShowOptions,
  ImageAiStorageFile,
  ImageGeneration,
  generateSessionId,
} from '../services/image-ai.service';
import { LogoImageService } from '../services/logo-image.service';
import {
  GenerateImageResponse,
  ImageAiApiService,
} from './services/image-ai-api.service';

const SLIDE_IN_ANIMATION_NAME = 'slide-in-right';
const SLIDE_IN_DURATION_MS = 600;
const DEFAULT_WIDTH = 960;
const DEFAULT_HEIGHT = 540;

export type ImageAiMessageType =
  | 'default'
  | 'no_credits'
  | 'error'
  | 'content_blocked';

export type ImageAiView = 'main' | 'feedback' | 'feedback-sent';

@Component({
  selector: 'component-image-ai',
  templateUrl: './image-ai.component.html',
  styleUrls: ['./image-ai.component.scss'],
  standalone: false,
})
export class ImageAiComponent implements OnInit, OnDestroy {
  @ViewChild('panel') panelRef: ElementRef<HTMLElement>;
  @ViewChild('promptInput') promptInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild('feedbackInput') feedbackInput: ElementRef<HTMLTextAreaElement>;

  readonly defaultImageThumbnail = LogoImageService.DEFAULT_IMAGE_THUMBNAIL;

  readonly pageSize = 10;

  prompt = '';
  loading = false;
  messageType: ImageAiMessageType | null = null;
  generations: ImageGeneration[] = [];
  sessionId: string | null = null;

  view: ImageAiView = 'main';
  feedbackText = '';

  private allGenerations: ImageGeneration[] = [];
  private feedbackTarget: ImageGeneration | null = null;

  private onSelect: ((file: ImageAiStorageFile) => void) | null = null;
  private loadAfterSlideHandler: ((event: Event) => void) | null = null;
  private loadAfterSlideFallback: ReturnType<typeof setTimeout> | null = null;
  private focusOnAnimationEnd: ((event: Event) => void) | null = null;
  private width = DEFAULT_WIDTH;
  private height = DEFAULT_HEIGHT;
  private activeRequestId = 0;
  private activeLoadId = 0;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private storageUtilsService: StorageUtilsService,
    private imageAiApi: ImageAiApiService,
    private imageAiService: ImageAiService,
    private templateEditorService: TemplateEditorService,
    private editorToolsLogging: EditorToolsLoggingService,
    private userStateService: UserStateService,
  ) {}

  ngOnInit(): void {
    this.componentsService.registerDirective({
      type: 'rise-image-generate-ai',
      element: this.elementRef.nativeElement,
      usesBackTitleHeader: () => true,
      show: (options?: ImageAiShowOptions) => {
        this.resetState();
        this.onSelect = options?.onSelect ?? null;
        this.width = options?.width ?? DEFAULT_WIDTH;
        this.height = options?.height ?? DEFAULT_HEIGHT;
        this.scheduleFocus();
        this.loadRecentGenerationsAfterSlide();
      },
      getLabel: (): string | undefined =>
        this.view === 'main' ? undefined : 'Send feedback',
      onBackHandler: (): boolean => {
        if (this.view === 'main') {
          this.onSelect = null;
          return false;
        }

        this.returnToMainView();
        return true;
      },
    });
  }

  ngOnDestroy(): void {
    this.onSelect = null;
    this.clearLoadAfterSlide();
    this.clearScheduledFocus();
    this.activeRequestId++;
  }

  get placeholderAspectRatio(): string {
    return `${this.width} / ${this.height}`;
  }

  get leftColumn(): ImageGeneration[] {
    return this.generations.filter((_, i) => i % 2 === 0);
  }

  get rightColumn(): ImageGeneration[] {
    return this.generations.filter((_, i) => i % 2 === 1);
  }

  canGenerate(): boolean {
    return !!this.prompt.trim() && !this.loading;
  }

  isErrorMessage(): boolean {
    return (
      this.messageType === 'error' || this.messageType === 'content_blocked'
    );
  }

  canAccessCompanySettings(): boolean {
    return (
      this.userStateService.isRiseAdmin() || this.userStateService.isUserAdmin()
    );
  }

  async onGenerate(): Promise<void> {
    if (!this.canGenerate() || !this.sessionId) {
      return;
    }

    this.loading = true;
    this.messageType = 'default';
    const requestId = ++this.activeRequestId;
    const prompt = this.prompt.trim();
    const startedAt = Date.now();

    try {
      const response = await this.imageAiApi.generateImage({
        prompt,
        width: this.width,
        height: this.height,
        sessionId: this.sessionId,
      });

      if (requestId !== this.activeRequestId) {
        return;
      }

      this.loading = false;

      if (
        response.type === 'no_credits' ||
        response.type === 'content_blocked'
      ) {
        this.messageType = response.type;
      } else {
        const generation = this.addGeneration(response);
        if (generation) {
          this.logGeneration(
            response,
            generation,
            prompt,
            Date.now() - startedAt,
          );
        }
      }
    } catch {
      if (requestId !== this.activeRequestId) {
        return;
      }

      this.loading = false;
      this.messageType = 'error';
    }
  }

  loadMore(): void {
    if (this.generations.length >= this.allGenerations.length) {
      return;
    }

    this.generations = this.allGenerations.slice(
      0,
      this.generations.length + this.pageSize,
    );
  }

  onSelectGeneration(generation: ImageGeneration): void {
    if (!this.onSelect) {
      return;
    }

    this.onSelect(this.buildStorageFile(generation.url));
  }

  onImageLoad(generation: ImageGeneration): void {
    generation.loaded = true;
  }

  onPreviewError(generation: ImageGeneration): void {
    if (generation.thumbnailUrl && !generation.usingFull) {
      generation.usingFull = true;
      generation.displaySrc = generation.url;
    } else {
      generation.previewFailed = true;
      generation.displaySrc = this.defaultImageThumbnail;
      generation.loaded = true;
    }
  }

  onThumbUp(generation: ImageGeneration, event: Event): void {
    event.stopPropagation();
    generation.feedbackRating = 'good';
    this.logFeedback(generation, 'good');
  }

  onThumbDown(generation: ImageGeneration, event: Event): void {
    event.stopPropagation();
    this.feedbackTarget = generation;
    this.feedbackText = '';
    this.view = 'feedback';
    this.scheduleFeedbackFocus();
  }

  canSendFeedback(): boolean {
    return !!this.feedbackText.trim();
  }

  onPromptEnter(event: Event): void {
    event.preventDefault();
    this.onGenerate();
  }

  onFeedbackEnter(event: Event): void {
    event.preventDefault();
    this.onSendFeedback();
  }

  onSendFeedback(): void {
    if (!this.canSendFeedback()) {
      return;
    }

    if (this.feedbackTarget) {
      this.feedbackTarget.feedbackRating = 'bad';
    }
    this.logFeedback(this.feedbackTarget, 'bad', this.feedbackText.trim());

    this.view = 'feedback-sent';
  }

  onFeedbackBack(): void {
    this.returnToMainView();
  }

  private returnToMainView(): void {
    this.view = 'main';
    this.feedbackText = '';
    this.feedbackTarget = null;
    this.scheduleFocus();
  }

  private async loadRecentGenerations(): Promise<void> {
    const loadId = ++this.activeLoadId;

    try {
      const generations = await this.imageAiService.getRecentGenerations();

      if (loadId === this.activeLoadId) {
        this.allGenerations = generations;
        this.generations = generations.slice(0, this.pageSize);
      }
    } catch {
      // Leave the grid empty when recent generations cannot be loaded.
    }
  }

  private addGeneration(
    response: GenerateImageResponse,
  ): ImageGeneration | null {
    if (response.type !== 'editor_ai_image') {
      return null;
    }

    const url = response.data.url;
    const generation: ImageGeneration = {
      url,
      imageId:
        response.data.imageId ??
        this.storageUtilsService.fileNameWithoutExtension(url),
      filename: this.storageUtilsService.fileNameOf(url),
      displaySrc: url,
      justGenerated: true,
    };
    this.allGenerations = [generation, ...this.allGenerations];
    this.generations = [generation, ...this.generations];

    return generation;
  }

  private logGeneration(
    response: GenerateImageResponse,
    generation: ImageGeneration,
    prompt: string,
    durationMs: number,
  ): void {
    if (
      response.type !== 'editor_ai_image' ||
      !this.sessionId ||
      !generation.imageId
    ) {
      return;
    }

    this.editorToolsLogging
      .logImageGenerated({
        sessionId: this.sessionId,
        presentationId: this.templateEditorService.presentation?.id,
        imageId: generation.imageId,
        prompt,
        model: response.data.model,
        imageUrl: generation.url,
        aspectRatio:
          response.data.aspectRatio ??
          ImageAiService.aspectRatioLabel(this.width, this.height),
        durationMs,
        creditsUsed: response.creditsUsed,
      })
      .subscribe();
  }

  private logFeedback(
    generation: ImageGeneration | null,
    rating: 'good' | 'bad',
    message?: string,
  ): void {
    if (!generation?.imageId) {
      return;
    }

    this.editorToolsLogging
      .logImageFeedback({
        sessionId: this.sessionId ?? undefined,
        imageId: generation.imageId,
        rating,
        message,
      })
      .subscribe();
  }

  private buildStorageFile(url: string): ImageAiStorageFile {
    return { name: this.storageUtilsService.fileNameFromUrl(url) };
  }

  private resetState(): void {
    this.activeRequestId++;
    this.prompt = '';
    this.loading = false;
    this.messageType = 'default';
    this.generations = [];
    this.allGenerations = [];

    this.sessionId = this.generateSessionId();
    this.view = 'main';
    this.feedbackText = '';
    this.feedbackTarget = null;
  }

  private loadRecentGenerationsAfterSlide(): void {
    this.clearLoadAfterSlide();

    const run = () => {
      this.clearLoadAfterSlide();
      this.loadRecentGenerations();
    };

    const el = this.panelRef?.nativeElement;
    if (el) {
      this.loadAfterSlideHandler = (event: Event) => {
        if (
          (event as AnimationEvent).animationName === SLIDE_IN_ANIMATION_NAME
        ) {
          run();
        }
      };
      el.addEventListener('animationend', this.loadAfterSlideHandler);
    }

    this.loadAfterSlideFallback = setTimeout(run, SLIDE_IN_DURATION_MS);
  }

  private clearLoadAfterSlide(): void {
    const el = this.panelRef?.nativeElement;
    if (el && this.loadAfterSlideHandler) {
      el.removeEventListener('animationend', this.loadAfterSlideHandler);
    }
    this.loadAfterSlideHandler = null;

    if (this.loadAfterSlideFallback !== null) {
      clearTimeout(this.loadAfterSlideFallback);
      this.loadAfterSlideFallback = null;
    }
  }

  private scheduleFocus(): void {
    const el = this.panelRef?.nativeElement;
    if (!el) return;

    this.clearScheduledFocus();

    this.focusOnAnimationEnd = (event: Event) => {
      if ((event as AnimationEvent).animationName !== SLIDE_IN_ANIMATION_NAME) {
        return;
      }
      this.clearScheduledFocus();
      this.promptInput?.nativeElement.focus({ preventScroll: true });
    };
    el.addEventListener('animationend', this.focusOnAnimationEnd);
  }

  private scheduleFeedbackFocus(): void {
    setTimeout(() => this.feedbackInput?.nativeElement.focus());
  }

  private clearScheduledFocus(): void {
    const el = this.panelRef?.nativeElement;
    if (el && this.focusOnAnimationEnd) {
      el.removeEventListener('animationend', this.focusOnAnimationEnd);
    }
    this.focusOnAnimationEnd = null;
  }

  private generateSessionId(): string {
    return generateSessionId();
  }
}
